import React, { useEffect, useRef, useState, Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRightIcon, ChevronDownIcon, ArrowRightIcon, MenuIcon, XIcon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { sectors, sectorsMenuCopy, type Bi } from '../data/sectors';
import { navSections, getNavSection, isSectionActive, type NavGroup, type NavSection, type NavSectionId } from '../data/navigation';
import { SECTOR_ICONS } from './sectors/sectorIcons';
import { contact, hasPhone } from '../data/contact';
import { trackEvent } from '../utils/analytics';
import { AnnouncementBar } from './AnnouncementBar';
export function Header() {
  const {
    lang,
    setLang,
    t
  } = useLanguage();
  const pick = (v: Bi) => lang === 'FR' ? v.fr : v.en;
  const [activeMenu, setActiveMenu] = useState<NavSectionId | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<Record<string, boolean>>({
    platform: true,
    sectors: true
  });
  const location = useLocation();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    setActiveMenu(null);
    setMobileMenuOpen(false);
  }, [location.pathname, location.hash]);
  const handleMouseEnter = (menu: NavSectionId | null) => {
    clearTimeout(timeoutRef.current);
    setActiveMenu(menu);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveMenu(null), 150);
  };
  const toggleMobileSection = (id: string) => setMobileExpanded(prev => ({
    ...prev,
    [id]: !prev[id]
  }));
  const closeAll = () => {
    setActiveMenu(null);
    setMobileMenuOpen(false);
  };
  const platform = getNavSection('platform');
  const sectorsNav = getNavSection('sectors');
  const platformOverview = platform.items.find(i => !i.group)!;
  const platformColumns = [1, 2].map(col => (platform.groups ?? []).filter(g => g.column === col));
  const renderPlatformGroup = (group: NavGroup, twoCols: boolean) => <div key={group.id} data-id="element-3610" data-mp="1wcehmam9pb07">
      <div className="font-sans text-[11px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-4" data-id="element-3611" data-mp="1wcxqz2xuqr2m">{pick(group.label)}</div>
      <ul className={twoCols ? 'grid grid-cols-2 gap-x-8 gap-y-2.5' : 'space-y-2.5'} data-id="element-3612" data-mp="1hni0zp65apnz">
        {platform.items.filter(i => i.group === group.id).map(item => {
        const current = location.pathname === item.to && !item.parent;
        return <li key={item.id} className={item.parent ? 'pl-3 ml-1 border-l border-[#E4E3F2]' : ''} data-id="element-3613" data-mp="1cq8eoie1yu4d">
                <Link to={item.to} onClick={closeAll} className={`font-sans transition-colors ${item.parent ? 'text-[13px]' : 'text-sm'} ${current ? 'text-[#4F46E5] font-medium' : item.parent ? 'text-[#121212]/50 hover:text-[#4F46E5]' : 'text-[#121212]/75 hover:text-[#4F46E5]'}`} data-id="element-3614" data-mp="yqgt9wl7ibgc">
                  {pick(item.label)}
                </Link>
              </li>;
      })}
      </ul>
    </div>;
  const renderListMenu = (section: NavSection) => <div className="grid grid-cols-[2fr_3fr] gap-12" data-id="element-3615" data-mp="hdqs14w4j7q9">
      <div className="pr-6 border-r border-[#EEEDF6]" data-id="element-3616" data-mp="w59bxds3tmew">
        <h3 className="font-serif text-2xl text-[#121212]" data-id="element-3617" data-mp="re13ajylm52s">{pick(section.label)}</h3>
      </div>
      <ul className="grid grid-cols-2 gap-3 content-start" data-id="element-3618" data-mp="1555yn9uxob7b">
        {section.items.map(item => <li key={item.id} data-id="element-3619" data-mp="106beo4l8cglm">
            <Link to={item.to} onClick={closeAll} className="group block h-full rounded-xl border border-[#ECEDF2] px-4 py-3.5 hover:border-[#4F46E5]/40 hover:bg-[#FAFAFE] transition-colors duration-150" data-id="element-3620" data-mp="1051dv7mzsuba">
              <span className="block font-sans text-[14px] font-semibold text-[#121A3A] group-hover:text-[#4F46E5] transition-colors duration-150" data-id="element-3621" data-mp="1a5ab8osq0ls9">{pick(item.label)}</span>
              {item.description && <span className="block mt-1 font-sans text-[12.5px] text-[#6B7288] leading-snug" data-id="element-3622" data-mp="3bwnihki5v5rh">{pick(item.description)}</span>}
            </Link>
          </li>)}
      </ul>
    </div>;
  return <motion.header initial={{
    opacity: 0,
    y: -20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3
  }} className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E4E6EC]" onMouseLeave={handleMouseLeave} data-id="element-3623" data-mp="27wp2i1fgqkn4">
      <AnnouncementBar data-id="element-3624" data-mp="2gxnaeut0vcaz" />
      <div className="max-w-[1400px] mx-auto px-6 h-24 flex items-center justify-between" data-id="element-3625" data-mp="1k85yvz7cyw1d">
        <Link to="/" className="flex flex-col items-start z-50 relative" aria-label={t('BCITI — Home', 'BCITI — Accueil')} onClick={closeAll} data-id="element-3626" data-mp="1k6cpwd99vzq7">
          <span className="flex items-center" data-id="element-3627" data-mp="1t7t12n7ww5vh">
            <img src="https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png" alt="BCITI" className="h-[5.25rem] w-auto object-contain" data-id="element-3628" data-mp="1t713x7yo2033" />
            <span className="ml-4 mr-1 text-[#121212]/25 font-sans text-3xl font-light select-none" aria-hidden="true" data-id="element-3629" data-mp="11gwozotoe599">
              |
            </span>
            <img src="https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png" alt="ATNA" className="h-[4.5rem] w-auto object-contain" data-id="element-3630" data-mp="xzideq3w61t8" />
          </span>
          <span className="-mt-2 font-sans text-[11px] md:text-xs text-[#121212]/70 tracking-[0.04em] whitespace-nowrap" data-id="element-3631" data-mp="sykuuceyz5r7">
            {t('Trusted Intelligence. Built to Act.', 'Une intelligence de confiance. Conçue pour agir.')}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center h-full" aria-label={t('Main navigation', 'Navigation principale')} data-id="element-3632" data-mp="f1xwc57dc7j6">
          {navSections.map(section => {
          const routeActive = isSectionActive(section, location.pathname);
          const open = activeMenu === section.id;
          const hasMenu = section.menu !== 'none';
          return <Link key={section.id} to={section.to} onClick={closeAll} onMouseEnter={() => handleMouseEnter(hasMenu ? section.id : null)} onFocus={() => handleMouseEnter(hasMenu ? section.id : null)} aria-current={routeActive ? 'page' : undefined} aria-haspopup={hasMenu ? 'true' : undefined} aria-expanded={hasMenu ? open : undefined} className={`relative h-full flex items-center gap-1 px-4 font-sans text-sm font-medium transition-colors duration-150 ${routeActive || open ? 'text-[#4F46E5]' : 'text-[#121212]/70 hover:text-[#121212]'}`} data-id="element-3633" data-mp="3sgc5nyemh0en">
                {pick(section.label)}
                {hasMenu && <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180 text-[#4F46E5]' : routeActive ? 'text-[#4F46E5]' : 'text-[#121212]/40'}`} aria-hidden="true" data-id="element-3634" data-mp="5cim9j0ia60m" />}
                {routeActive && <span className="absolute left-4 bottom-7 h-[2px] w-5 rounded-full bg-[#4F46E5]" aria-hidden="true" data-id="element-3635" data-mp="3rqim88z1hext" />}
              </Link>;
        })}
        </nav>

        <div className="hidden lg:flex items-center gap-6 z-50 relative" data-id="element-3636" data-mp="o534m6ikwsq6">
          <div className="flex items-center font-sans text-xs font-semibold tracking-wider" aria-label={t('Language', 'Langue')} data-id="element-3637" data-mp="28svzw5xbil7i">
            {(['FR', 'EN'] as const).map((code, i) => <Fragment key={code} data-id="element-3638" data-mp="2iiyta78cq16d" data-mpi={i}>
                {i === 1 && <span className="mx-2 text-[#121212]/20" data-id="element-3639" data-mp="2iltalpw2j5vk">|</span>}
                <button onClick={() => setLang(code)} aria-pressed={lang === code} className={lang === code ? 'text-[#121212]' : 'text-[#121212]/40 hover:text-[#121212]/70 transition-colors'} data-id="element-3640" data-mp="2dlff17bnloef">
                  {code}
                </button>
              </Fragment>)}
          </div>

          {hasPhone() && <a href={`tel:${contact.phone}`} onClick={() => trackEvent('contact_click', {
          method: 'phone',
          location: 'header'
        })} className="hidden 2xl:inline font-sans text-sm font-medium text-[#121212]/70 hover:text-[#4F46E5] transition-colors whitespace-nowrap" data-id="element-3641" data-mp="1ytwt6sw44zn5">
              {contact.phoneLabel}
            </a>}

          <Link to="/request-demo" onClick={() => trackEvent('demo_cta_click', {
          location: 'header'
        })} className="flex items-center gap-2 px-6 py-2.5 bg-[#4F46E5] text-white text-sm font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-3642" data-mp="13n5t77g2w8wp">
            {t('Request a demo', 'Demander une démo')}
            <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-3643" data-mp="1icbud0yh8978" />
          </Link>
        </div>

        <button className="lg:hidden z-50 relative p-2 -mr-2 text-[#121212]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={mobileMenuOpen ? t('Close menu', 'Fermer le menu') : t('Open menu', 'Ouvrir le menu')} aria-expanded={mobileMenuOpen} data-id="element-3644" data-mp="1mdx2ya3za1bm">
          {mobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-3645" data-mp="21ao2cjdnjd18" /> : <MenuIcon className="w-6 h-6" data-id="element-3646" data-mp="21axx0k9n68d9" />}
        </button>
      </div>

      <AnimatePresence data-id="element-3647" data-mp="1xk1yo1bz1xx">
        {activeMenu && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute top-full left-0 right-0 bg-white border-b border-[#E4E6EC] shadow-xl shadow-black/5 overflow-hidden" onMouseEnter={() => handleMouseEnter(activeMenu)} onMouseLeave={handleMouseLeave} data-id="element-3648" data-mp="1z4u6706a8sm">
            <div className="max-w-[1200px] mx-auto px-6 py-10" data-id="element-3649" data-mp="26gb4pkbuh34v">
              {activeMenu === 'platform' && <div data-id="element-3650" data-mp="334r2u4t0sktl">
                  <div className="grid grid-cols-[2fr_3fr] gap-12 mb-10" data-id="element-3651" data-mp="334ziu5kfw690">
                    <div className="pr-6 border-r border-[#EEEDF6]" data-id="element-3652" data-mp="2z58iba542bra">
                      <h3 className="font-serif text-2xl text-[#121212] mb-6" data-id="element-3653" data-mp="2u4uey5egfpzl">{t('BCITI | ATNA platform', 'Plateforme BCITI | ATNA')}</h3>
                      <Link to={platformOverview.to} onClick={closeAll} className="group block mb-6 pb-6 border-b border-[#E4E6EC]" data-id="element-3654" data-mp="2p57yln3bxtmk">
                        <div className={`font-sans text-sm font-semibold transition-colors group-hover:text-[#4F46E5] ${location.pathname === platformOverview.to ? 'text-[#4F46E5]' : 'text-[#121212]'}`} data-id="element-3655" data-mp="1inktxefogpw6">{pick(platformOverview.label)}</div>
                        <div className="font-sans text-xs text-[#121212]/50 mt-1" data-id="element-3656" data-mp="184m634sgzzt5">{t('Everything connects to a verified core.', 'Tout converge vers un noyau vérifié.')}</div>
                      </Link>
                      {platformColumns[0].map(group => renderPlatformGroup(group, false))}
                    </div>
                    <div className="space-y-8" data-id="element-3657" data-mp="1vs41daay1g0v">{platformColumns[1].map(group => renderPlatformGroup(group, true))}</div>
                  </div>
                  <div className="flex items-center justify-between pt-6 border-t border-[#E4E6EC]" data-id="element-3658" data-mp="1qwosyxvpgcbu">
                    <p className="font-sans text-sm font-medium text-[#121212]/50" data-id="element-3659" data-mp="20siayws70y10">
                      {t('Identity powers communications. Communications power services. Services power intelligence.', 'L’identité alimente les communications. Les communications alimentent les services. Les services alimentent l’intelligence.')}
                    </p>
                    <Link to="/platform" onClick={closeAll} className="flex items-center gap-2 font-sans text-sm font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-3660" data-mp="2i35hws93bmgk">
                      {t('Explore the platform', 'Explorer la plateforme')} <ArrowRightIcon className="w-4 h-4" data-id="element-3661" data-mp="28a6ngtqwx1p1" />
                    </Link>
                  </div>
                </div>}

              {activeMenu === 'sectors' && <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-12" data-id="element-3662" data-mp="1zdpe9w23zdvx">
                  <div className="md:pr-6 md:border-r md:border-[#EEEDF6]" data-id="element-3663" data-mp="23e9bt0qlgjil">
                    <p className="font-sans text-[11px] font-semibold tracking-[0.24em] uppercase text-[#4F46E5]" data-id="element-3664" data-mp="1peglv7mw67bo">{pick(sectorsMenuCopy.eyebrow)}</p>
                    <p className="mt-4 font-serif text-[24px] leading-[1.15]" data-id="element-3665" data-mp="3jr6gxsozxmkn">
                      {sectorsMenuCopy.headline.map((l, i) => <span key={l.fr} className={`block ${i === 1 ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-3666" data-mp="3ooy82khth4ns" data-mpi={i}>{pick(l)}</span>)}
                    </p>
                    <p className="mt-5 font-sans text-[13.5px] text-[#4A5268] leading-relaxed" data-id="element-3667" data-mp="3i9hj6urroxmw">
                      {sectorsMenuCopy.signature.map(s => <span key={s.fr} className="block" data-id="element-3668" data-mp="3dd0n9eebtez9">{pick(s)}</span>)}
                    </p>
                    <Link to={sectorsNav.to} onClick={closeAll} className="mt-7 inline-flex items-center gap-2 font-sans text-sm font-medium text-[#4F46E5] hover:text-[#4338CA] transition-colors" data-id="element-3669" data-mp="9u7jftbco4ia">
                      {pick(sectorsNav.items[0].label)}
                      <ArrowRightIcon className="w-4 h-4" data-id="element-3670" data-mp="2tidh3r2wn1" />
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-3 content-start" data-id="element-3671" data-mp="3rafg6t3sg4vz">
                    {sectorsNav.items.slice(1).map(item => {
                const s = sectors.find(x => x.key === item.id)!;
                const Icon = SECTOR_ICONS[s.key];
                return <Link key={item.id} to={item.to} onClick={closeAll} className={`group flex items-start gap-3.5 rounded-xl border px-4 py-3.5 transition-colors duration-150 ${s.proven ? 'col-span-2 border-[#4F46E5]/25 bg-[#F6F5FE] hover:border-[#4F46E5]/50' : 'border-[#ECEDF2] hover:border-[#4F46E5]/40 hover:bg-[#FAFAFE]'}`} data-id="element-3672" data-mp="w4foxy3i8za1">
                          <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${s.proven ? 'bg-[#4F46E5]' : 'bg-[#F1F0FD]'}`} data-id="element-3673" data-mp="1f1qn1fdehmnw">
                            <Icon className={`w-4 h-4 ${s.proven ? 'text-white' : 'text-[#4F46E5]'}`} strokeWidth={1.7} data-id="element-3674" data-mp="1j1994egyk3sb" />
                          </span>
                          <span className="min-w-0 flex-1" data-id="element-3675" data-mp="1steydgfts73t">
                            <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1" data-id="element-3676" data-mp="1suzx7i96ac7h">
                              <span className="font-sans text-[14px] font-semibold text-[#121A3A] group-hover:text-[#4F46E5] transition-colors duration-150" data-id="element-3677" data-mp="1xpvnodyrgrih">{pick(item.label)}</span>
                              {s.proven && <span className="rounded-full bg-white border border-[#DCDAF7] px-2 py-0.5 font-sans text-[9.5px] font-semibold tracking-[0.14em] uppercase text-[#4F46E5] whitespace-nowrap" data-id="element-3678" data-mp="2ujmy8i0kyml8">{pick(sectorsMenuCopy.badge)}</span>}
                            </span>
                            <span className="block mt-1 font-sans text-[12.5px] text-[#6B7288] leading-snug" data-id="element-3679" data-mp="2gj2ffluo0qrq">{pick(s.descriptor)}</span>
                          </span>
                        </Link>;
              })}
                  </div>
                </div>}

              {activeMenu === 'trust' && renderListMenu(getNavSection('trust'))}
              {activeMenu === 'company' && renderListMenu(getNavSection('company'))}
            </div>
          </motion.div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-3680" data-mp="rwp9gzxwuv3">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: '100vh'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-[#E4E6EC] overflow-y-auto pb-24" data-id="element-3681" data-mp="8bfqz4gyrct0">
            <div className="p-6 border-b border-[#E4E6EC] bg-white sticky top-0 z-10" data-id="element-3682" data-mp="3ccl5ht5t2jz">
              <Link to="/request-demo" onClick={() => {
            trackEvent('demo_cta_click', {
              location: 'mobile_menu'
            });
            closeAll();
          }} className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#4F46E5] text-white text-sm font-medium rounded-none" data-id="element-3683" data-mp="r2qgbh71jhis">
                {t('Request a demo', 'Demander une démo')}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-3684" data-mp="mb8giwgr7mze" />
              </Link>
              {hasPhone() && <a href={`tel:${contact.phone}`} onClick={() => trackEvent('contact_click', {
            method: 'phone',
            location: 'mobile_menu'
          })} className="mt-3 flex items-center justify-center w-full py-3 border border-[#E4E6EC] font-sans text-sm font-medium text-[#121212]" data-id="element-3685" data-mp="1a0j7jm8ay30c">
                  {t('Call', 'Appeler')} {contact.phoneLabel}
                </a>}
            </div>

            <nav className="px-6 py-4" aria-label={t('Main navigation', 'Navigation principale')} data-id="element-3686" data-mp="1fmaatrycxcyp">
              {navSections.map(section => {
            const expanded = !!mobileExpanded[section.id];
            const routeActive = isSectionActive(section, location.pathname);
            return <div key={section.id} className="border-b border-[#E4E6EC] last:border-0" data-id="element-3687" data-mp="10ww0agj1ophq">
                    <button onClick={() => toggleMobileSection(section.id)} aria-expanded={expanded} className="flex items-center justify-between w-full py-5 text-left" data-id="element-3688" data-mp="w05lrxmw21yf">
                      <span className={`font-serif text-xl ${routeActive ? 'text-[#4F46E5]' : 'text-[#121212]'}`} data-id="element-3689" data-mp="mz7gzd4mlqes">{pick(section.label)}</span>
                      <ChevronDownIcon className={`w-5 h-5 text-[#121212]/40 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} data-id="element-3690" data-mp="i0cx07ux9vt3" />
                    </button>
                    <AnimatePresence initial={false} data-id="element-3691" data-mp="2ykycibo4nfcj">
                      {expanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} className="overflow-hidden" data-id="element-3692" data-mp="33m5nejlkgunf">
                          <div className="pb-6 pl-4 space-y-4 border-l-2 border-[#E4E6EC] ml-2" data-id="element-3693" data-mp="2axqac2lzwmeq">
                            {section.groups ? <>
                                {section.items.filter(i => !i.group).map(item => <Link key={item.id} to={item.to} onClick={closeAll} className="block font-sans text-sm font-semibold text-[#121212]" data-id="element-3694" data-mp="2opofk2cl5y5m">
                                      {pick(item.label)}
                                    </Link>)}
                                {section.groups.map(group => <div key={group.id} className="pt-1" data-id="element-3695" data-mp="kmf5bg8xbqvr">
                                    <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-3" data-id="element-3696" data-mp="zixkfck5av05">{pick(group.label)}</div>
                                    <div className="space-y-3" data-id="element-3697" data-mp="ule5sbjjcg7o">
                                      {section.items.filter(i => i.group === group.id).map(item => <Link key={item.id} to={item.to} onClick={closeAll} className={`block font-sans ${item.parent ? 'text-[13px] pl-3 ml-1 border-l border-[#E4E3F2]' : 'text-sm'} ${location.pathname === item.to && !item.parent ? 'text-[#4F46E5] font-medium' : item.parent ? 'text-[#121212]/50' : 'text-[#121212]/70'}`} data-id="element-3698" data-mp="1i7ubz8o5r9eo">
                                            {pick(item.label)}
                                          </Link>)}
                                    </div>
                                  </div>)}
                              </> : section.items.map((item, i) => <Link key={item.id} to={item.to} onClick={closeAll} className={`block font-sans text-sm ${i === 0 && section.items.length > 1 ? 'font-semibold text-[#121212]' : 'text-[#121212]/70'}`} data-id="element-3699" data-mp="21ws7kfqkx4re" data-mpi={i}>
                                  {pick(item.label)}
                                </Link>)}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
          })}
            </nav>

            <div className="px-6 py-8 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-3700" data-mp="194p0kyhni83g">
              <div className="flex items-center gap-4 font-sans text-sm font-medium" data-id="element-3701" data-mp="18aczucq7s7ke">
                <span className="text-[#121212]/40" data-id="element-3702" data-mp="1ccpuo63fal2e">{t('Language:', 'Langue :')}</span>
                {(['FR', 'EN'] as const).map(code => <button key={code} onClick={() => setLang(code)} aria-pressed={lang === code} className={lang === code ? 'text-[#121212]' : 'text-[#121212]/40'} data-id="element-3703" data-mp="ye9oqhddwylb">
                    {code}
                  </button>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.header>;
}